import React, { useEffect, useState, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router';
import AdminLayout from '../../components/admin/AdminLayout';
import { useToast } from '../../contexts/ToastContext';
import { getPublicSettings, updateSystemSettings } from '../../api/client';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Skeleton from '../../components/ui/Skeleton';
import {
  Save,
  RefreshCw,
  Globe,
  Home,
  Info,
  Phone,
  LogIn,
  Palette,
  ChevronDown,
  ChevronRight,
  Flag,
  Users,
  BookOpen,
  FolderGit2,
  Target,
  Tags,
  UserCircle,
  MessageSquareQuote,
  Scale,
  HelpCircle,
  Zap,
  PanelBottom,
} from 'lucide-react';

interface SettingItem {
  key: string;
  value: string;
  group_name: string;
  is_public: number;
}

const groupIcons: Record<string, React.ElementType> = {
  general: Globe,
  homepage: Home,
  about: Info,
  contact: Phone,
  login: LogIn,
  theme: Palette,
  landing_hero: Flag,
  landing_social: Users,
  landing_problem: HelpCircle,
  landing_highlights: Zap,
  landing_curriculum: BookOpen,
  landing_projects: FolderGit2,
  landing_audience: Target,
  landing_pricing: Tags,
  landing_instructor: UserCircle,
  landing_testimonials: MessageSquareQuote,
  landing_comparison: Scale,
  landing_faq: HelpCircle,
  landing_enroll: Zap,
  landing_footer: PanelBottom,
};

const groupLabels: Record<string, string> = {
  general: 'General',
  homepage: 'Homepage',
  about: 'About Page',
  contact: 'Contact Page',
  login: 'Login Page',
  theme: 'Theme',
  landing_hero: '🏠 Landing — Hero',
  landing_social: '🏠 Landing — Social Proof',
  landing_problem: '🏠 Landing — Problem',
  landing_highlights: '🏠 Landing — Highlights',
  landing_curriculum: '🏠 Landing — Curriculum',
  landing_projects: '🏠 Landing — Projects',
  landing_audience: '🏠 Landing — Audience',
  landing_pricing: '🏠 Landing — Pricing',
  landing_instructor: '🏠 Landing — Instructor',
  landing_testimonials: '🏠 Landing — Testimonials',
  landing_comparison: '🏠 Landing — Comparison',
  landing_faq: '🏠 Landing — FAQ',
  landing_enroll: '🏠 Landing — Enroll CTA',
  landing_footer: '🏠 Landing — Footer',
};

export default function AdminCMS(): React.JSX.Element {
  const toast = useToast();
  const location = useLocation();
  const groupRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [settings, setSettings] = useState<SettingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['general', 'homepage']));
  const [search, setSearch] = useState('');

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPublicSettings();
      const list = Array.isArray(res.settings)
        ? res.settings
        : Array.isArray(res.data?.settings)
          ? res.data.settings
          : [];
      setSettings(list as SettingItem[]);
    } catch (err: any) {
      toast.show(err.message || 'Failed to load settings', 'error');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Handle hash-based navigation from sidebar
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash) {
      setExpandedGroups((prev) => {
        const next = new Set(prev);
        next.add(hash);
        return next;
      });
      // Scroll to the group after it renders
      setTimeout(() => {
        const el = groupRefs.current[hash];
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    }
  }, [location.hash]);

  const toggleGroup = (group: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  };

  const updateValue = (key: string, value: string) => {
    setSettings((prev) => prev.map((s) => (s.key === key ? { ...s, value } : s)));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSystemSettings({
        settings: settings.map((s) => ({
          key: s.key,
          value: s.value,
          group_name: s.group_name,
          is_public: !!s.is_public,
        })),
      });
      toast.show('Settings saved successfully', 'success');
    } catch (err: any) {
      toast.show(err.message || 'Failed to save settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const grouped = settings.reduce<Record<string, SettingItem[]>>((acc, s) => {
    if (!acc[s.group_name]) acc[s.group_name] = [];
    acc[s.group_name].push(s);
    return acc;
  }, {});

  const groupOrder = [
    'general',
    'homepage',
    'landing_hero',
    'landing_social',
    'landing_problem',
    'landing_highlights',
    'landing_curriculum',
    'landing_projects',
    'landing_audience',
    'landing_pricing',
    'landing_instructor',
    'landing_testimonials',
    'landing_comparison',
    'landing_faq',
    'landing_enroll',
    'landing_footer',
    'about',
    'contact',
    'login',
    'theme',
  ];
  const sortedGroups = groupOrder.filter((g) => grouped[g]).sort((a, b) => {
    const ai = groupOrder.indexOf(a);
    const bi = groupOrder.indexOf(b);
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
  });

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">CMS Settings</h1>
            <p className="text-sm text-slate-500">Manage all frontend content from one place</p>
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={fetchSettings} leftIcon={<RefreshCw className="w-4 h-4" />}>
              Refresh
            </Button>
            <Button onClick={handleSave} isLoading={saving} leftIcon={<Save className="w-4 h-4" />}>
              Save All Changes
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <input
            type="text"
            placeholder="Search settings..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
          />
        </div>

        {/* Settings Groups */}
        <div className="space-y-4">
          {loading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-100 p-6">
                <Skeleton className="h-6 w-32 mb-4" />
                <div className="space-y-3">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              </div>
            ))
          ) : (
            sortedGroups.map((group) => {
              const GroupIcon = groupIcons[group] || Globe;
              const isExpanded = expandedGroups.has(group);
              const groupSettings = grouped[group].filter(
                (s) =>
                  !search ||
                  s.key.toLowerCase().includes(search.toLowerCase()) ||
                  s.value.toLowerCase().includes(search.toLowerCase())
              );
              if (groupSettings.length === 0) return null;

              return (
                <motion.div
                  key={group}
                  ref={(el) => { groupRefs.current[group] = el; }}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-2xl border border-slate-100 overflow-hidden"
                >
                  <button
                    onClick={() => toggleGroup(group)}
                    className="w-full flex items-center justify-between px-6 py-4 hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center">
                        <GroupIcon className="w-4 h-4 text-amber-500" />
                      </div>
                      <div className="text-left">
                        <h3 className="font-bold text-slate-900 text-sm">{groupLabels[group] || group}</h3>
                        <p className="text-xs text-slate-500">{groupSettings.length} settings</p>
                      </div>
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    )}
                  </button>

                  {isExpanded && (
                    <div className="px-6 pb-6 space-y-4">
                      {groupSettings.map((s) => {
                        const isJson = s.value.trim().startsWith('[') || s.value.trim().startsWith('{');
                        const isLong = s.value.length > 100 || isJson;
                        return (
                          <div key={s.key}>
                            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                              {s.key}
                            </label>
                            {isLong ? (
                              <textarea
                                value={s.value}
                                onChange={(e) => updateValue(s.key, e.target.value)}
                                rows={isJson ? 6 : 3}
                                className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-900 font-mono focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 resize-y"
                              />
                            ) : (
                              <Input
                                value={s.value}
                                onChange={(e) => updateValue(s.key, e.target.value)}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </motion.div>
              );
            })
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
